import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowDownRight, MapPin, Phone } from "lucide-react";
import { IMAGES, scrollToSection, telHref } from "@/lib/scroll";
import { useSettings } from "@/lib/SettingsContext";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const settings = useSettings();
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [5, -5]), {
    stiffness: 70,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-5, 5]), {
    stiffness: 70,
    damping: 18,
  });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseMove={onMove}
      className="relative overflow-hidden pt-32 pb-20 lg:min-h-screen lg:pt-40 lg:pb-28"
    >
      <div
        aria-hidden
        className="absolute -top-32 right-[-10%] h-[34rem] w-[34rem] rounded-full bg-[#F3EFEA]"
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="mb-8 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-[#1B523F]"
          >
            <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" strokeWidth={2} />
            Harlow, Essex — CM20 3HS
          </motion.p>

          <h1 className="font-serif text-[13.5vw] font-light leading-[0.98] tracking-tight text-[#121A16] sm:text-6xl lg:text-[5.6rem]">
            <MaskedLine delay={0.35}>The art of</MaskedLine>
            <MaskedLine delay={0.5}>
              <em className="font-normal italic text-[#0F382A]">pristine</em>
            </MaskedLine>
            <MaskedLine delay={0.65}>
              living<span className="text-[#D4AF37]">.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-8 max-w-md text-base leading-relaxed text-[#4A5A52] sm:text-lg"
          >
            Meticulous domestic and commercial cleaning, delivered with
            white-glove care across Harlow and Essex. Insured, vetted, and
            audited on every visit.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-button"
              onClick={() => scrollToSection("#pricing")}
              className="group flex items-center gap-2 rounded-full bg-[#0F382A] px-8 py-4 text-sm font-semibold text-[#FAF8F5] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#1B523F]"
            >
              Estimate your clean
              <ArrowDownRight
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5"
                strokeWidth={2}
              />
            </button>
            <a
              data-testid="hero-phone-link"
              href={telHref(settings.phone_display)}
              className="group flex items-center gap-3 rounded-full border border-[#0F382A]/20 px-8 py-4 text-sm font-semibold text-[#0F382A] transition-[border-color,background-color] duration-300 hover:border-[#0F382A] hover:bg-[#0F382A]/5"
            >
              <Phone className="h-4 w-4" strokeWidth={1.75} />
              {settings.phone_display}
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5" style={{ perspective: 1200 }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.6, ease: EASE }}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative"
          >
            <div
              aria-hidden
              className="absolute -inset-3 translate-x-5 translate-y-5 rounded-[2rem] border border-[#D4AF37]/50"
            />
            <div className="relative overflow-hidden rounded-[2rem] shadow-[0_40px_80px_-30px_rgba(11,29,22,0.4)]">
              <img
                src={IMAGES.hero}
                alt="Pristine living room cleaned by Don Sparkle"
                data-testid="hero-image"
                className="aspect-[4/5] w-full object-cover"
              />
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(120% 90% at 30% 10%, rgba(212,175,55,0.12) 0%, rgba(11,29,22,0.35) 100%)",
                }}
              />
              <div className="absolute bottom-5 left-5 rounded-full bg-[#FAF8F5]/85 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#0F382A] backdrop-blur-md">
                Quarry Spring · Harlow
              </div>
            </div>
            <div className="animate-float-soft absolute -left-8 top-10 hidden rounded-2xl border border-[#E5DEC3] bg-[#FAF8F5]/90 px-6 py-4 shadow-lg backdrop-blur-md lg:block">
              <p className="font-serif text-3xl font-medium text-[#0F382A]">500+</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#4A5A52]">
                Homes sparkled
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
